import { useCallback, useRef, useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Music } from "lucide-react";
import gggLogo from "@/assets/logos/ggg-logo-final.webp";
import MobileMenu from "@/components/MobileMenu";
import { useScrolled } from "@/hooks/useScrolled";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { useSoundscape } from "@/hooks/useSoundscape";
import { NAV_LINKS } from "@/data/navigation";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  // Header transitions from transparent to blurred ~40-60px into the scroll.
  const scrolled = useScrolled(50);
  const location = useLocation();
  const menuRef = useRef(null);
  const firstLinkRef = useRef(null);

  // Ambient soundscape control -- lives here (rather than a floating
  // bottom-right pill) so a single "Music" toggle can be shared by both
  // the desktop nav CTA and the mobile menu's CTA below.
  const { audioRef, hasEntered, playing, enterWithSound, togglePlay } = useSoundscape();
  const toggleMusic = useCallback(() => {
    if (!hasEntered) {
      enterWithSound();
    } else {
      togglePlay();
    }
  }, [hasEntered, enterWithSound, togglePlay]);

  const closeMenu = useCallback(() => setOpen(false), []);

  // Close the mobile menu on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useFocusTrap({ open, containerRef: menuRef, initialFocusRef: firstLinkRef, onClose: closeMenu });

  return (
    <>
      <header
        className={`site-header ${scrolled ? "site-header-scrolled" : ""}`}
        data-testid="site-header"
      >
        <audio ref={audioRef} src="/assets/media/ambient-theme.mp3" loop preload="auto" />

        <div className="page-shell site-header-inner flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-home-logo-link"
            className="header-logo-link flex items-center shrink-0"
            aria-label="Gopi Gadhvi Group — Home"
          >
            <img src={gggLogo} alt="Gopi Gadhvi Group" className="header-logo" />
          </Link>

          <nav className="nav-links-desktop" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}
              >
                {link.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={toggleMusic}
              data-testid="nav-music-toggle-btn"
              aria-label={playing ? "Pause ambient music" : "Play ambient music"}
              aria-pressed={playing}
              className="btn-nav-cta gap-2"
            >
              <Music size={16} /> Music
            </button>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="nav-mobile-toggle hamburger-btn items-center justify-center"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Rendered as a SIBLING of <header>, not a descendant -- when the
          header is scrolled, it gets `backdrop-filter: blur(...)`, and
          per spec, filter/backdrop-filter establishes a new containing
          block for `position: fixed` descendants. Keeping this fixed,
          full-viewport overlay outside that subtree avoids that
          containing-block reassignment entirely, which was causing the
          menu's opaque background to render incorrectly (appearing
          transparent) once the page had been scrolled before opening
          it. */}
      <MobileMenu
        navLinks={NAV_LINKS}
        open={open}
        menuRef={menuRef}
        firstLinkRef={firstLinkRef}
        onClose={closeMenu}
        onMusicToggle={toggleMusic}
        musicPlaying={playing}
      />
    </>
  );
};

export default Navbar;
